import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import useAuthStore from '../../store/authStore';
import { EyeIcon, EyeSlashIcon, LockClosedIcon, ArrowLeftIcon, SparklesIcon } from '../../lib/commonUtils';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetPassword, isLoading } = useAuthStore();

  const email = location.state?.email || '';
  const otp_code = location.state?.otp || '';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      new_password: '',
      confirm_new_password: '',
    },
  });

  const newPwd = watch('new_password');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // If we don't have email or otp, someone might have navigated here directly
  React.useEffect(() => {
    if (!email || !otp_code) {
      toast.error('Session expired or invalid. Please start over.');
      navigate('/forgot-password');
    }
  }, [email, otp_code, navigate]);

  const onSubmit = async (data) => {
    const res = await resetPassword({
      email,
      otp_code,
      new_password: data.new_password,
      confirm_new_password: data.confirm_new_password,
    });

    if (res.success) {
      toast.success('Password updated successfully! Please login with your new password.');
      navigate('/login');
    } else {
      toast.error(res.error || 'Password update failed.');
    }
  };

  return (
    <div className="w-full">
      <div className="text-left mb-8">
        <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">Reset Password</h2>
        <p className="text-gray-400 font-medium tracking-tight">
          Create a new strong password for your account.
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-1.5">
          <label className="block text-sm font-bold text-gray-300 ml-1">New Password</label>
          <div className="relative">
            <input
              {...register('new_password', {
                required: 'New password is required',
                minLength: { value: 8, message: 'Minimum 8 characters' },
              })}
              type={showNew ? 'text' : 'password'}
              className={`block w-full px-5 py-3.5 pr-12 bg-[#1A1F26] border ${errors.new_password ? 'border-red-500' : 'border-white/5'} rounded-[1rem] text-white placeholder-gray-500 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all shadow-lg text-sm`}
              placeholder="••••••••"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
              onClick={() => setShowNew(!showNew)}
            >
              {showNew ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
            </button>
          </div>
          {errors.new_password && <p className="mt-1 text-xs font-semibold text-red-500 ml-1">{errors.new_password.message}</p>}
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-bold text-gray-300 ml-1">Confirm New Password</label>
          <div className="relative">
            <input
              {...register('confirm_new_password', {
                required: 'Confirmation is required',
                validate: (v) => v === newPwd || 'Passwords do not match',
              })}
              type={showConfirm ? 'text' : 'password'}
              className={`block w-full px-5 py-3.5 pr-12 bg-[#1A1F26] border ${errors.confirm_new_password ? 'border-red-500' : 'border-white/5'} rounded-[1rem] text-white placeholder-gray-500 focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all shadow-lg text-sm`}
              placeholder="••••••••"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
              onClick={() => setShowConfirm(!showConfirm)}
            >
              {showConfirm ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
            </button>
          </div>
          {errors.confirm_new_password && <p className="mt-1 text-xs font-semibold text-red-500 ml-1">{errors.confirm_new_password.message}</p>}
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-3.5 px-6 rounded-[1rem] shadow-[0_20px_40px_-12px_rgba(79,70,229,0.4)] text-base font-bold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-4 focus:ring-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <>
                <SparklesIcon className="h-5 w-5 mr-2" />
                Update Password
              </>
            )}
          </button>
        </div>

        <p className="text-center pt-6">
          <Link to="/login" className="text-xs font-bold text-gray-400 hover:text-primary-500 transition-colors inline-flex items-center">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to login
          </Link>
        </p>
      </form>
    </div>
  );
};

export default ResetPassword;
